import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Spinner from '@risk-smart/themed-cloudscape-components/spinner';
import Button from '@risksmart-app/components/src/button';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import { PageLayout } from 'src/layouts';
import { useGetDetailPath } from 'src/routes/useGetDetailParentPath';

import { useDeleteCustomRole } from '@/hooks/mutations/custom-role';
import { useGetCustomRoleById, useGetDefaultRoles } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { settingsCustomRolesUrl } from '@/utils/urls';

import { useTabs } from './config';

type Props = {
  activeTabId: 'details';
};

const Page: FC<Props> = ({ activeTabId }) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'customRoles' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const customRoleId = useGetGuidParam('customRoleId');
  const detailPath = useGetDetailPath(customRoleId);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const navigate = useNavigate();
  const { data: getRolesResponse, loading: loadingRoles } = useGetDefaultRoles({
    queryArgs: {},
  });
  const { data, loading } = useGetCustomRoleById({
    queryArgs: { id: customRoleId },
  });

  const { deleteCustomRole, loading: deleteLoading } = useDeleteCustomRole();

  const customRole = data?.custom_role[0];
  const tabs = useTabs(
    customRole,
    getRolesResponse?.auth_role_type
      ?.filter(
        (c) =>
          c.resourceTypes.length > 0 &&
          (c.InstanceRoleKey === 'Owner' || c.InstanceRoleKey === 'Reader')
      )
      .map((role) => ({
        roleKey: role.RoleKey,
        name: role.Name,
        groupKey: role.resourceTypes[0].resourceType.ResourceType,
        category: role.Category === 'Manager' ? 'Manager' : 'Viewer',
      })) || [],
    detailPath
  );

  const onDelete = useDeleteResultNotification({
    entityName: t('entity_name'),
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
    asyncAction: async () => {
      await deleteCustomRole({ id: customRoleId });
      setIsDeleteModalVisible(false);
      navigate(settingsCustomRolesUrl());

      return true;
    },
  });

  // Object-level toolbar (CUS-175) — flag-gated. Replaces the PageLayout header
  // with the object toolbar; the Delete action moves into the toolbar's danger
  // slot. Legacy `actions` below stay as the flag-off fallback.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title: customRole?.RoleName ?? '' },
      actions: {
        danger: [
          {
            id: 'delete',
            label: t('delete_button'),
            iconName: 'trash-01',
            onClick: () => setIsDeleteModalVisible(true),
          },
        ],
      },
    }),
    [customRole?.RoleName, t]
  );

  return (
    <PageLayout
      title={customRole?.RoleName}
      meta={{
        title: t('tab_title'),
      }}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            variant={'normal'}
            formAction={'none'}
            onClick={() => {
              setIsDeleteModalVisible(true);
            }}
          >
            {t('delete_button')}
          </Button>
        </SpaceBetween>
      }
    >
      {loading || loadingRoles ? (
        <Spinner />
      ) : (
        <ControlledTabs
          activeTabId={activeTabId}
          tabs={tabs}
          variant={'container'}
        />
      )}
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete_button')}
        onDelete={onDelete}
        onDismiss={() => {
          setIsDeleteModalVisible(false);
        }}
      >
        {t('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
